import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { CountrySelect } from 'components/ui/country-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'components/ui/command';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from 'lib/utils';
import { Badge } from 'components/ui/badge';
import { ACCOUNT_QUERY_KEY } from 'features/profile/hooks/use-account';
import { useOrganizations } from 'features/organization/hooks/use-organization';
import { useGetRolesQuery, useCreateBulkUsers } from 'features/iam/hooks/use-iam';
import { useTranslation } from 'react-i18next';
import { UserSelector } from 'features/organization/components/add-organization/user-selector';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { ROLES } from 'constant/roles';
import { ORGANIZATION_CONSTANTS } from 'constant/organization';

const MAX_USERS = 5;
const MAX_ROLES = 3;

type AddMultipleUsersProps = {
  onClose: () => void;
};

export const AddMultipleUsers: React.FC<AddMultipleUsersProps> = ({ onClose }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [currentRole, setCurrentRole] = useState('');
  const [country, setCountry] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [organizationSearchTerm, setOrganizationSearchTerm] = useState('');
  const [organizationOpen, setOrganizationOpen] = useState(false);

  const isAdmin = useCommonStore((state) => state.isAdmin);
  const isOrgAdmin = useCommonStore((state) => state.isOrgAdmin);

  const [availableRoles, setAvailableRoles] = useState<
    Array<{
      name: string;
      slug: string;
    }>
  >([]);

  const { data: rolesData, isLoading: isLoadingRoles } = useGetRolesQuery({
    page: 0,
    pageSize: 100,
    filter: { search: '' },
    sort: { property: 'name', isDescending: false },
  });

  // Fetch organizations for autocomplete
  const { data: organizationsData } = useOrganizations({
    page: 0,
    pageSize: 50,
    name: organizationSearchTerm,
    enabled: true,
  });

  const organizations = (organizationsData?.data || []).filter(
    org => org.name !== ORGANIZATION_CONSTANTS.DEFAULT_ORGANIZATION_NAME
  );
  const selectedOrganization = organizations.find(org => org.itemId === organizationId);

  useEffect(() => {
    if (rolesData?.data) {
      const mappedRoles = rolesData.data
        .filter(role => {
          if (isOrgAdmin) {
            // remove app-user and admin roles for org-admin users from UI
            return role.name !== 'app-user' && role.name !== 'admin';
          }
          // remove app-user role for admin users from UI
          return role.name !== 'app-user';
        })
        .map((role) => ({
          name: role.name,
          slug: role.name.toLowerCase().replace(/\s+/g, '-'),
        }));
      if (mappedRoles) setAvailableRoles(mappedRoles);
    }
  }, [isOrgAdmin, rolesData]);

  const { mutate: createBulkUsers } = useCreateBulkUsers({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEY });
      await queryClient.refetchQueries({
        queryKey: ACCOUNT_QUERY_KEY,
        type: 'active',
        exact: false,
      });
      onClose();
      window.location.reload();
    },
  });

  const handleEmailsChange = (emails: string[]) => {
    // Only take the first MAX_USERS emails if more are provided
    const limitedEmails = emails.slice(0, MAX_USERS);
    setSelectedEmails(limitedEmails);
  };

  const handleAddRole = (roleSlug: string) => {
    if (!roleSlug) return;

    if (selectedRoles.length >= MAX_ROLES) return;

    if (!selectedRoles.includes(roleSlug)) {
      const newRoles = [...selectedRoles, roleSlug];
      setSelectedRoles(newRoles);

      // Clear organization if admin role is selected
      if (roleSlug === "admin" && isAdmin) {
        setOrganizationId('');
      }
    }
    setCurrentRole('');
  };

  const handleRemoveRole = (roleToRemove: string) => {
    setSelectedRoles(selectedRoles.filter((role) => role !== roleToRemove));
  };

  const getAvailableRoles = () => {
    return availableRoles.filter((role) => !selectedRoles.includes(role.slug));
  };

  const isMaxRolesReached = () => {
    return selectedRoles.length >= MAX_ROLES;
  };

  const handleSubmit = async () => {
    // Create bulk users with the new API
    const tempRoles = [...selectedRoles];
    tempRoles.push('app-user');

    const payload = {
      emails: selectedEmails,
      roles: tempRoles,
      country: country,
      organizationIds: isAdmin ? (organizationId === "" ? [] : [organizationId]) : organizationsData?.data?.map((org) => org.itemId) || [],
    };

    createBulkUsers(payload);
  };

  const handleDialogClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const isOrgAdminOrUser = selectedRoles.includes(ROLES.ORG_ADMIN) || selectedRoles.includes(ROLES.ORG_USER);

  return (
    <DialogContent
      className="rounded-[10px] sm:max-w-[700px] overflow-y-auto max-h-screen"
      onClick={handleDialogClick}
    >
      <DialogHeader>
        <DialogTitle className="mb-2">{t('ADD_MULTIPLE_USERS')}</DialogTitle>
        <DialogDescription className="text-medium-emphasis font-normal">
          {t('ADD_MULTIPLE_USERS_DESCRIPTION')}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        {/* Email Selector - Full Width at Top */}
        <div>
          <Label className="text-sm font-medium mb-2 block">{t('EMAIL_ADDRESSES')}</Label>
          <UserSelector
            onEmailsChange={handleEmailsChange}
            maxEmails={MAX_USERS}
          />
        </div>

        {/* Role and Organization Selector Side by Side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Role Selector */}
          <div>
            <Label className="text-sm font-medium mb-2 block">
              {t('ROLES')}{isOrgAdmin ? "*" : ""}
            </Label>
            <div className="space-y-2">
              <Select
                onValueChange={(value) => {
                  handleAddRole(value);
                }}
                value={currentRole}
                disabled={isLoadingRoles || isMaxRolesReached()}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      isLoadingRoles
                        ? `${t('LOADING_ROLES')}...`
                        : isMaxRolesReached()
                          ? t('MAX_ROLES_REACHED')
                          : t('SELECT_ROLES')
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableRoles().map((role) => (
                    <SelectItem key={role.slug} value={role.slug}>
                      <span>{t(role.slug?.toUpperCase())}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex flex-wrap gap-2 mt-2">
                {selectedRoles.map((roleSlug) => (
                  <Badge
                    key={roleSlug}
                    className="pr-1 flex items-center gap-1 text-white hover:bg-primary"
                  >
                    <span>{t(roleSlug?.toUpperCase())}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-4 w-4 p-0 hover:bg-transparent"
                      onClick={() => handleRemoveRole(roleSlug)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {/* Organization Selector - Only for admin users */}
          {isAdmin && (
            <div>
              <Label className="text-sm font-medium mb-2 block">{t('ORGANIZATION')} {isOrgAdminOrUser ? "*" : ""}</Label>
              <Popover open={organizationOpen} onOpenChange={setOrganizationOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={organizationOpen}
                    className={cn(
                      "w-full justify-between h-11",
                      !organizationId && "text-muted-foreground"
                    )}
                    disabled={selectedRoles.includes(ROLES.ADMIN) || !selectedRoles.length}
                  >
                    {selectedOrganization ? selectedOrganization.name : t('SELECT_ORGANIZATION')}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command className="overflow-hidden">
                    <CommandInput
                      placeholder={t('SEARCH_ORGANIZATIONS')}
                      value={organizationSearchTerm}
                      onValueChange={setOrganizationSearchTerm}
                    />
                    <CommandList className="h-[200px] overflow-auto py-2">
                      <CommandEmpty>{t('NO_ORGANIZATIONS_FOUND')}</CommandEmpty>
                      <CommandGroup>
                        {organizations.map((organization) => (
                          <CommandItem
                            key={organization.itemId}
                            value={organization.name}
                            onSelect={() => {
                              setOrganizationId(organization.itemId);
                              setOrganizationOpen(false);
                            }}
                            className="cursor-pointer"
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4 shrink-0",
                                organizationId === organization.itemId ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <div className="flex flex-col min-w-0 flex-1">
                              <span className="font-medium truncate">{organization.name}</span>
                              <span className="text-sm text-muted-foreground truncate">{organization.email}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {selectedRoles.includes(ROLES.ADMIN) && (
                <p className="text-sm text-yellow-600 mt-2">
                  {t('ADMIN_ORG_WARNING')}
                </p>
              )}

              {isOrgAdminOrUser && !organizationId && (
                <p className="text-sm text-error mt-2">
                  {t('ORG_ADMIN_ORG_USER_ORG_REQUIRED')}
                </p>
              )}
            </div>
          )}

          {/* Empty placeholder when not admin to maintain grid layout */}
          {!isAdmin && <div></div>}
        </div>



        {/* Country Selector - Full Width */}
        <div>
          <Label className="text-sm font-medium mb-2 block">{t('COUNTRY')}*</Label>
          <CountrySelect
            value={country}
            onChange={setCountry}
            placeholder={t('SELECT_COUNTRY') || 'Select country'}
          />
          {!country && selectedEmails.length > 0 && (
            <p className="text-sm text-destructive mt-1">
              {t('COUNTRY_REQUIRED') || 'Country is required'}
            </p>
          )}
        </div>
      </div>

      <DialogFooter className="mt-5 flex justify-end gap-2">
        <Button variant="outline" type="button" onClick={onClose}>
          {t('CANCEL')}
        </Button>
        <Button
          type="submit"
          onClick={handleSubmit}
          disabled={selectedEmails.length === 0 || !country || (isOrgAdmin && selectedRoles.length === 0)}
        >
          {t('INVITE_USERS')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}; 